import React, { Component } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import { datadogRum } from '@datadog/browser-rum'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fad } from '@fortawesome/pro-duotone-svg-icons'
import { far } from '@fortawesome/pro-regular-svg-icons'
import { GoogleOAuthProvider } from '@react-oauth/google'
import PropTypes from 'prop-types'
import { SuperTokensWrapper } from 'supertokens-auth-react'

import { _ScrollToTop, ScrollToTop } from 'app/_ScrollToTop'
import { AccountUserContextProvider } from 'app/AccountUserContextProvider'
import { AppContextProvider } from 'app/AppContextProvider'
import { AppUserContextProvider } from 'app/AppUserContextProvider'
import { CustomCss } from 'app/CustomCss'
import { CustomScripts } from 'app/CustomScripts'
import { catchAppErrors } from 'app/errorBoundaryFunctions'
import MetadataLoading from 'app/MetadataLoading'
import PreserveQuery from 'app/PreserveQuery'
import settings, { GOOGLE_OAUTH_CLIENT_ID } from 'app/settings'
import UserMonitoring from 'app/UserMonitoring'
import { WorkspaceDomainRedirector } from 'app/WorkspaceDomainRedirector'
import queryClient from 'data/reactQueryCache'
import { RealtimeUpdatesWatcher } from 'data/realtime/RealtimeUpdatesWatcher'
import { WebsocketListener } from 'data/websockets/WebsocketListener'
import AstraTrialBlocker from 'features/auth/stacker-auth/AstraTrialBlocker'
import SessionExpiredModal from 'features/auth/stacker-auth/SessionExpiredModal'
import { initializeSupertokens } from 'features/auth/supertokens'
import { PreviewAsModal } from 'features/core/PreviewingBar'
import { LoadActionObserver } from 'features/loadActions'
import { NavigationContextProvider } from 'features/navigation/NavigationContextProvider'
import { Omnibar } from 'features/Omnibar/Omnibar'
import { PreviewServiceContextProvider } from 'features/PreviewService/PreviewServiceContextProvider'
import ProfileSettingsModal from 'features/profile/ProfileSettingsModal'
import { AlgoliaInsights } from 'features/Search/InsightsEvents'
import { SupportMovedModal } from 'features/support/SupportMovedModal'
import { LayoutEditorContextProvider as SlowLayoutEditorContextProvider } from 'features/utils/LayoutEditorContext'
import { LayoutEditorContextProvider } from 'features/views/LayoutEditor/LayoutEditorContext'
import { LayoutEditorDnDContext } from 'features/views/LayoutEditor/LayoutEditorDnDContext'
import AdminSideTray from 'features/workspace/AdminSideTray'
import WorkspaceSettingsModal from 'features/workspace/WorkspaceSettingsModal'
// Analytics instance
import AnalyticsIdentifier from 'utils/AnalyticsIdentifier'
import PerformanceMonitor from 'utils/PerformanceMonitor'

import { ToastProvider } from 'ui/components/Toast'
import { ThemeProvider } from 'ui/styling/themes/ThemeProvider'

import { AuthContextProvider } from './AuthContext/AuthContextProvider'
import GlobalModals from './GlobalModals'
import { GlobalStyle } from './GlobalStyle'
import LDFlagsWatcher from './LDFlagsWatcher'
import MetaDataRefresher from './MetaDataRefresher'
import { PostHog } from './PostHog'
import { PylonChatBot } from './PylonChatBot'
import { Routes } from './Routes'
import UserActivityTracker from './UserActivityTracker'
import { WorkspaceContextProvider } from './WorkspaceContextProvider'

import 'ui/helpers/reset.css'
import 'ui/styling/GlobalStyles.css'
import '@fortawesome/fontawesome-pro/css/all.css'

// We need to import these globally to use for the icon pickers
library.add(fad, far)
const getUserConfirmation = (message, callback) => {
    console.warn('Confirming')
    window.confirm(callback)
}

initializeSupertokens()

export class Root extends Component {
    state = {}

    currentInterval = null

    UNSAFE_componentWillMount() {
        // Wipe everything out from local storage whenever we log in as support to avoid issues with
        // old stored tokens and such.
        if (window.location.href.match(/admin\/support-login\?api_token=[A-Za-z0-9.]+$/)) {
            localStorage.clear()
        }

        // Set up Datadog RUM, unless when running locally
        if (!settings.IS_LOCAL_INSTANCE) {
            datadogRum.init({
                applicationId: settings.DD.RUM_APP_ID,
                clientToken: settings.DD.RUM_TOKEN,
                version: settings.VERSION,
                site: 'datadoghq.com',
                service: settings.DD.SERVICE,
                env: settings.DOMAIN,
                sessionSampleRate: 100,
                sessionReplaySampleRate: 0,
                trackResources: true,
                trackLongTasks: true,
                trackUserInteractions: true,
                trackSessionAcrossSubdomains: true,
                useSecureSessionCookie: true, // Disable this to test on local instances
                allowedTracingUrls: [
                    { match: settings.BACKEND_ENDPOINT, propagatorTypes: ['tracecontext'] },
                ],
            })
        }
    }

    componentWillUnmount() {
        if (this.currentInterval) {
            clearInterval(this.currentInterval)
        }
    }

    render() {
        const { store } = this.props

        const routes = catchAppErrors(<Routes />)

        return (
            <SuperTokensWrapper>
                <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    <QueryClientProvider client={queryClient}>
                        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
                        <DndProvider backend={HTML5Backend}>
                            <Provider store={store}>
                                <ToastContainer />
                                <ToastProvider>
                                    <BrowserRouter getUserConfirmation={getUserConfirmation}>
                                        <PerformanceMonitor />
                                        <LDFlagsWatcher />
                                        <AuthContextProvider>
                                            <WorkspaceContextProvider>
                                                <AppContextProvider>
                                                    <MetadataLoading>
                                                        <AccountUserContextProvider>
                                                            <PreviewServiceContextProvider>
                                                                <AppUserContextProvider>
                                                                    <WorkspaceDomainRedirector />
                                                                    <UserActivityTracker />
                                                                    <AnalyticsIdentifier />
                                                                    <MetaDataRefresher />
                                                                    <PostHog />
                                                                    <RealtimeUpdatesWatcher />
                                                                    <WebsocketListener />
                                                                    <ThemeProvider theme="admin">
                                                                        <GlobalStyle>
                                                                            <SlowLayoutEditorContextProvider>
                                                                                <LayoutEditorContextProvider>
                                                                                    <LayoutEditorDnDContext>
                                                                                        <GlobalModals>
                                                                                            <NavigationContextProvider>
                                                                                                <Omnibar>
                                                                                                    <AlgoliaInsights />
                                                                                                    <AdminSideTray />
                                                                                                    <PreserveQuery />
                                                                                                    <PylonChatBot />
                                                                                                    <CustomScripts />
                                                                                                    <CustomCss />
                                                                                                    <SupportMovedModal />
                                                                                                    <WorkspaceSettingsModal />
                                                                                                    <ProfileSettingsModal />
                                                                                                    <PreviewAsModal />
                                                                                                    <ThemeProvider theme="default">
                                                                                                        <ScrollToTop>
                                                                                                            {
                                                                                                                routes
                                                                                                            }
                                                                                                        </ScrollToTop>
                                                                                                    </ThemeProvider>
                                                                                                    <LoadActionObserver />
                                                                                                    <UserMonitoring />
                                                                                                    <AstraTrialBlocker />
                                                                                                    <SessionExpiredModal />
                                                                                                </Omnibar>
                                                                                            </NavigationContextProvider>
                                                                                        </GlobalModals>
                                                                                    </LayoutEditorDnDContext>
                                                                                </LayoutEditorContextProvider>
                                                                            </SlowLayoutEditorContextProvider>
                                                                        </GlobalStyle>
                                                                    </ThemeProvider>
                                                                </AppUserContextProvider>
                                                            </PreviewServiceContextProvider>
                                                        </AccountUserContextProvider>
                                                    </MetadataLoading>
                                                </AppContextProvider>
                                            </WorkspaceContextProvider>
                                        </AuthContextProvider>
                                    </BrowserRouter>
                                </ToastProvider>
                            </Provider>
                        </DndProvider>
                    </QueryClientProvider>
                </GoogleOAuthProvider>
            </SuperTokensWrapper>
        )
    }
}

Root.propTypes = {
    store: PropTypes.object.isRequired,
}

_ScrollToTop.propTypes = {
    children: PropTypes.element.isRequired,
    location: PropTypes.object.isRequired, // From withRouter
}
